import React, { forwardRef } from 'react';
import {
  tokens,
  makeStyles,
  Button,
  Text,
  Spinner,
  Tooltip,
  Persona,
} from '@fluentui/react-components';
import {
  NavigationRegular,
  WeatherMoonRegular,
  WeatherSunnyRegular,
  BugRegular,
  SettingsRegular,
} from '@fluentui/react-icons';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { loginRequest } from '../authConfig';
import androidChromeIcon from '../assets/android-chrome-512x512.png';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXL}`,
    backgroundColor: '#2B3240',
    color: '#FFFFFF',
    boxShadow: tokens.shadow8,
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1001,
    '@media (max-width: 768px)': {
      padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    },
  },
  hamburgerButton: {
    display: 'none',
    color: tokens.colorBrandForeground1,
    marginRight: tokens.spacingHorizontalM,
    '@media (max-width: 768px)': {
      display: 'inline-flex',
    },
  },
  headerTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexGrow: 1,
    flexShrink: 2,
    minWidth: 0,
    overflow: 'hidden',
  },
  pageTitleText: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    letterSpacing: '0.5px',
    color: tokens.colorBrandForeground1,
    textTransform: 'uppercase',
    fontFamily: '"Agency FB", sans-serif',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
    '@media (max-width: 575px)': {
      display: 'none',
    },
  },
  headerLogo: {
    height: '40px',
    width: '40px',
    borderRadius: tokens.borderRadiusCircular,
    background: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
    flexShrink: 1,
    flexGrow: 0,
    minWidth: 0,
    flexWrap: 'nowrap',
    '@media (max-width: 768px)': {
      gap: tokens.spacingHorizontalXS,
    },
  },
  themeToggle: {
    color: '#FFFFFF',
    minWidth: '36px',
    flexShrink: 0,
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    } as Record<string, string>,
  },
  logoutButton: {
    minWidth: 'auto',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    '@media (max-width: 768px)': {
      paddingLeft: tokens.spacingHorizontalS,
      paddingRight: tokens.spacingHorizontalS,
    },
    '@media (max-width: 400px)': {
      paddingLeft: tokens.spacingHorizontalXS,
      paddingRight: tokens.spacingHorizontalXS,
      fontSize: tokens.fontSizeBase200,
    },
  },
  personaName: {
    fontWeight: tokens.fontWeightRegular,
    flexShrink: 1,
    minWidth: 0,
    overflow: 'hidden',
    '& .fui-Persona__primaryText': {
      color: `${tokens.colorNeutralForegroundInverted} !important`,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '@media (max-width: 768px)': {
        display: 'none',
      },
    },
    '& .fui-Persona__secondaryText': {
      color: `${tokens.colorNeutralForegroundInverted} !important`,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      '@media (max-width: 768px)': {
        display: 'none',
      },
    },
  },
});

const getInitials = (name?: string | null): string => {
  if (!name) return '';
  const names = name.split(' ');
  return names.map(n => n[0]).join('').toUpperCase().substring(0, 2);
};

interface HeaderBarProps {
  account: any;
  profilePhoto?: string;
  isMobileView?: boolean;
  onToggleSidebar?: () => void;
  isDarkMode?: boolean;
  onToggleTheme?: () => void;
  selectedModel?: string;
  activeAgentName?: string;
  onOpenSettings?: () => void;
}

export const HeaderBar = forwardRef<HTMLDivElement, HeaderBarProps>(
  ({ account, profilePhoto, isMobileView, onToggleSidebar, isDarkMode, onToggleTheme, selectedModel, activeAgentName, onOpenSettings }, ref) => {
    const styles = useStyles();
    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const handleLogin = () => {
      instance.loginPopup(loginRequest).catch(e => console.error("Login failed:", e));
    };

    const handleLogout = () => {
      instance.logoutPopup({ mainWindowRedirectUri: "/" });
    };

    const handleReportIssue = () => {
      const body = [
        '## Environment',
        `- **User:** ${account?.username || 'Unknown'}`,
        `- **Model:** ${selectedModel || 'Unknown'}`,
        `- **Agent:** ${activeAgentName || 'Unknown'}`,
        `- **Browser:** ${navigator.userAgent}`,
        `- **Timestamp:** ${new Date().toISOString()}`,
        '',
        '## Description',
        '<!-- Please describe the issue -->',
        '',
        '## Steps to Reproduce',
        '1. ',
        '2. ',
        '3. ',
        '',
        '## Expected Behavior',
        '',
        '',
        '## Actual Behavior',
        '',
      ].join('\n');

      const params = new URLSearchParams({
        title: '[PaceChat Bug] ',
        body,
        labels: 'bug',
      });
      window.open(`https://github.com/Pace-Applied-Solutions/paceChat/issues/new?${params.toString()}`, '_blank');
    };

    const personaAvatar = profilePhoto
      ? { image: { src: profilePhoto } }
      : { initials: getInitials(account?.name || account?.username), color: 'colorful' as 'colorful' };

    return (
      <div className={styles.header} ref={ref}>
        <div className={styles.headerTitleContainer}>
          {isMobileView && (
            <Button
              icon={<NavigationRegular fontSize={24} />}
              appearance="transparent"
              className={styles.hamburgerButton}
              onClick={onToggleSidebar}
              aria-label="Toggle navigation"
            />
          )}
          <img src={androidChromeIcon} alt="Pace AI Logo" className={styles.headerLogo} />
          <Text className={styles.pageTitleText}>Pace Chat</Text>
        </div>
        <div className={styles.headerActions}>
          {onToggleTheme && (
            <Tooltip content={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"} relationship="label">
              <Button
                icon={isDarkMode ? <WeatherSunnyRegular fontSize={20} /> : <WeatherMoonRegular fontSize={20} />}
                appearance="subtle"
                onClick={onToggleTheme}
                aria-label={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                className={styles.themeToggle}
              />
            </Tooltip>
          )}
          {isAuthenticated && (
            <Tooltip content="Report an Issue" relationship="label">
              <Button
                icon={<BugRegular fontSize={20} />}
                appearance="subtle"
                onClick={handleReportIssue}
                aria-label="Report an Issue"
                className={styles.themeToggle}
              />
            </Tooltip>
          )}
          {isAuthenticated && onOpenSettings && (
            <Tooltip content="Settings" relationship="label">
              <Button
                icon={<SettingsRegular fontSize={20} />}
                appearance="subtle"
                onClick={onOpenSettings}
                aria-label="Settings"
                className={styles.themeToggle}
              />
            </Tooltip>
          )}
          {isAuthenticated && account ? (
            <>
              <Persona
                name={account.name || account.username}
                secondaryText={account.username}
                avatar={personaAvatar}
                className={styles.personaName}
              />
              <Button appearance="primary" onClick={handleLogout} className={styles.logoutButton}>
                Logout
              </Button>
            </>
          ) : (
            <Button
              appearance="primary"
              onClick={handleLogin}
              disabled={inProgress !== InteractionStatus.None}
            >
              {inProgress !== InteractionStatus.None ? <Spinner size="tiny" /> : "Login"}
            </Button>
          )}
        </div>
      </div>
    );
  }
);
